import { keyOutWhiteBackgroundPixels, litSquadItemSpecies } from '@fias/litsquad-avatar';
import type { LitSquadInventoryEntry, LitSquadInventoryItemDetail } from '@/lib/api';

/**
 * Presence check for a key on an item-metadata object. LitSquad metadata
 * uses the legacy PascalCase keys (`Eyes`, `AvatarItemGroup`) for both
 * imported and crafted items, so names match exactly.
 */
function hasMetadataField(meta: Record<string, unknown> | null | undefined, name: string): boolean {
  return !!meta && Object.prototype.hasOwnProperty.call(meta, name);
}

/**
 * Returns `true` if a LitSquad inventory entry represents a playable
 * character (an avatar base) and `false` if it represents a crafted or
 * legacy-imported accessory.
 *
 * Legacy import buckets BOTH characters and accessories under the same
 * 17 `litdef_species_*` definition ids, so the def-id alone can't
 * discriminate. The shape of the per-item `metadata` is the source of
 * truth:
 *
 *   - Characters carry the rendering attributes `Eyes`, `Ears`, `Face`,
 *     `Tail`, etc., and never have an `AvatarItemGroup`.
 *   - Accessories carry an `AvatarItemGroup` (the legacy server's own
 *     discriminator) and never have body-part attributes.
 *
 * If metadata is missing or ambiguous (e.g. admin-minted items without a
 * legacy stats payload), the display name pattern is used as a fallback:
 *
 *   - `"LitSquad <Species> <number>"`   → character
 *   - `"LitSquad <Species> <words...>"` → accessory
 *
 * The default when no signal is present is "character" — this matches
 * how plain `litdef_species_*` rows are seeded by the migration.
 */
export function isLitSquadCharacter(
  entry: Pick<LitSquadInventoryEntry, 'itemMetadata' | 'displayName'>,
): boolean {
  const meta = entry.itemMetadata;
  if (hasMetadataField(meta, 'Eyes')) return true;
  if (hasMetadataField(meta, 'AvatarItemGroup')) return false;

  const name = entry.displayName?.trim();
  if (name) {
    // Characters look like "LitSquad Dog 100018"; accessories look like
    // "LitSquad Dog Golf Club". After the species token, characters have
    // a numeric instance suffix; accessories have one or more words.
    const match = name.match(/^LitSquad\s+\S+\s+(.+)$/i);
    if (match) {
      return /^\d+$/.test(match[1].trim());
    }
    // "LitSquad Dog" with no suffix — admin-minted character template.
    if (/^LitSquad\s+\S+$/i.test(name)) return true;
  }

  return true;
}

/**
 * Validates that a URI is a safe HTTP(S) URL suitable for use as an image src.
 */
export function isSafeImageUri(uri: string | null | undefined): boolean {
  if (!uri) return false;
  try {
    const url = new URL(typeof uri === 'string' ? uri.trim() : uri);
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
}

/**
 * Preferred display name for a LitSquad inventory row. Falls back to the
 * species-level definition name when the item has no per-item override
 * (e.g. admin-minted items without a legacy asset_name).
 */
export function resolveLitSquadDisplayName(
  entry: Pick<LitSquadInventoryEntry, 'displayName' | 'speciesName'>,
): string {
  return entry.displayName && entry.displayName.trim().length > 0
    ? entry.displayName
    : entry.speciesName;
}

/**
 * Preferred thumbnail for a LitSquad inventory row — the item-level override
 * (legacy icon rehosted under arche_dfe_icons/LitSquad/) takes precedence
 * over the species-level definition default.
 */
export function resolveLitSquadThumbnail(
  entry: Pick<LitSquadInventoryEntry, 'thumbnailUri' | 'definitionThumbnailUri'>,
): string | null {
  return entry.thumbnailUri ?? entry.definitionThumbnailUri ?? null;
}

export function resolveLitSquadItemDisplayName(
  item: Pick<LitSquadInventoryItemDetail, 'displayName'>,
  speciesName: string | null,
): string {
  if (item.displayName && item.displayName.trim().length > 0) return item.displayName;
  return speciesName ?? 'LitSquad NFT';
}

/**
 * The species ("dog", "bear", "crab", …) of a LitSquad inventory entry,
 * lowercase, or `null` when it has none.
 *
 * Gates accessory layering in the avatar editor: only accessories whose
 * species matches the base character's fit its silhouette (a Crab snowboard
 * wouldn't render right on a Dog), and the species picks where each accessory
 * is drawn. It is the server avatar pipeline's own rule (`litSquadItemSpecies`
 * from `@fias/litsquad-avatar`) — the editor never guesses a species the
 * server would not, so it offers exactly the accessories a save accepts and
 * previews them where the saved avatar will have them. `null` means the entry
 * fits no species-gated UI, never "matches everything".
 */
export function getLitSquadSpecies(
  entry: Pick<LitSquadInventoryEntry, 'itemDefId' | 'itemMetadata'>,
): string | null {
  return litSquadItemSpecies({ itemDefId: entry.itemDefId, metadata: entry.itemMetadata });
}

// =============================================================================
// White-background keying for legacy JPEG accessory thumbnails
// =============================================================================
//
/**
 * DOM wrapper for {@link keyOutWhiteBackgroundPixels}. Draws `img` at its
 * natural size onto an offscreen canvas, runs the edge-flood chroma key,
 * and returns the canvas (which is itself a valid `CanvasImageSource` for
 * later `drawImage` calls).
 *
 * If the environment can't provide a 2D context or `getImageData` throws
 * (jsdom no-op contexts in tests, CORS-tainted canvases), falls back to
 * returning the original image untouched — the caller still gets a
 * usable `CanvasImageSource`.
 */
export function keyOutWhiteBackground(img: HTMLImageElement): CanvasImageSource {
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  if (!w || !h) return img;

  let canvas: HTMLCanvasElement;
  try {
    canvas = document.createElement('canvas');
  } catch {
    return img;
  }
  canvas.width = w;
  canvas.height = h;

  const ctx = canvas.getContext('2d');
  if (!ctx) return img;
  ctx.drawImage(img, 0, 0);

  let imageData: ImageData;
  try {
    imageData = ctx.getImageData(0, 0, w, h);
  } catch {
    // jsdom returns a non-functional context whose getImageData throws,
    // and a CORS-tainted canvas throws SecurityError. Either way we have
    // no pixel access — let the caller draw the raw image.
    return img;
  }

  keyOutWhiteBackgroundPixels(imageData);
  ctx.putImageData(imageData, 0, 0);
  return canvas;
}
